<template>
  <div class="attendee-table-skeleton" aria-busy="true" :aria-label="adminText('workflowsAll.loading_attendees_78e3f8b', 'Loading attendees')">
    <div v-if="showFilters" class="skeleton-filters">
      <div class="skeleton-filter-buttons">
        <vue-skeleton-loader rounded radius="8" width="48px" height="48px" animation="fade" />
        <vue-skeleton-loader rounded radius="8" width="48px" height="48px" animation="fade" />
      </div>
      <div class="skeleton-search">
        <vue-skeleton-loader rounded radius="8" width="100%" height="48px" animation="fade" />
      </div>
    </div>
    <div class="skeleton-table-wrapper">
      <div class="skeleton-table-grid skeleton-table-header" :style="gridStyle">
        <vue-skeleton-loader rounded radius="4" width="18px" height="18px" animation="fade" />
        <vue-skeleton-loader
          v-for="(field, index) in skeletonFields"
          :key="`header-${field}-${index}`"
          rounded
          radius="4"
          width="70%"
          height="18px"
          animation="fade"
        />
        <div class="skeleton-action-space" />
      </div>
      <div
        v-for="row in rowCount"
        :key="`row-${row}`"
        class="skeleton-table-grid skeleton-table-row"
        :style="gridStyle"
      >
        <vue-skeleton-loader rounded radius="4" width="18px" height="18px" animation="fade" />
        <vue-skeleton-loader
          v-for="(field, index) in skeletonFields"
          :key="`cell-${row}-${field}-${index}`"
          rounded
          radius="4"
          :width="cellWidth(index)"
          height="16px"
          animation="fade"
        />
        <div class="skeleton-row-actions">
          <vue-skeleton-loader rounded radius="18" width="36px" height="36px" animation="fade" />
          <vue-skeleton-loader rounded radius="18" width="36px" height="36px" animation="fade" />
        </div>
      </div>
    </div>
    <div class="skeleton-pagination">
      <vue-skeleton-loader rounded radius="4" width="32px" height="16px" animation="fade" />
      <div class="skeleton-pagination-right">
        <vue-skeleton-loader rounded radius="4" width="92px" height="16px" animation="fade" />
        <vue-skeleton-loader rounded radius="16" width="32px" height="32px" animation="fade" />
        <vue-skeleton-loader rounded radius="16" width="32px" height="32px" animation="fade" />
      </div>
    </div>
  </div>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
const fallbackFields = [
  'Attendee name',
  'Owner Email',
  'Document ID',
  'Function',
  'Ticket Buyer',
  'Created At',
]

export default {
  mixins: [WorkflowDisplay],
  name: 'table-list-skeleton',
  props: {
    showFilters: {
      type: Boolean,
      default: true,
    },
    visibleFields: {
      type: Array,
      default: () => [],
    },
    rowCount: {
      type: Number,
      default: 6,
    },
  },
  computed: {
    skeletonFields() {
      const fields = this.visibleFields && this.visibleFields.length ? this.visibleFields : fallbackFields

      return fields.slice(0, 10)
    },
    gridStyle() {
      return {
        gridTemplateColumns: `24px ${this.skeletonFields
          .map(field => this.columnWidth(field))
          .join(' ')} 104px`,
      }
    },
  },
  methods: {
    columnWidth(field) {
      const widths = {
        'Attendee name': '200px',
        Email: '240px',
        email: '240px',
        'Owner Email': '240px',
        'Ticket Buyer': '200px',
        'Buyer Email': '260px',
        'Document ID': '140px',
        Function: '160px',
        Category: '160px',
        Address: '220px',
        address: '220px',
        City: '150px',
        city: '150px',
        'Postal Code': '140px',
        'Postal code': '140px',
        postalCode: '140px',
        zipCode: '140px',
        code: '120px',
        Code: '120px',
        Country: '160px',
        country: '160px',
        'Created At': '120px',
      }

      return widths[field] || '180px'
    },
    cellWidth(index) {
      const widths = ['82%', '68%', '56%', '74%', '62%', '48%']

      return widths[index % widths.length]
    },
  },
}
</script>

<style scoped>
.attendee-table-skeleton {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  overflow: hidden;
  font-family: 'Source Sans Pro', sans-serif;
}

.skeleton-filters {
  display: flex;
  align-items: stretch;
  width: 100%;
  min-height: 48px;
  margin-bottom: 2px;
  overflow: hidden;
  background-color: #f5f5f5;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
}

.skeleton-filter-buttons {
  display: flex;
  flex-shrink: 0;
  gap: 2px;
}

.skeleton-search {
  flex: 1;
  min-width: 0;
}

.skeleton-table-wrapper {
  display: block;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  contain: inline-size;
  overflow-x: auto;
  overflow-y: hidden;
  border-bottom: 1px solid #f0f6fa;
  -webkit-overflow-scrolling: touch;
}

.skeleton-table-wrapper::-webkit-scrollbar {
  height: 8px;
}

.skeleton-table-wrapper::-webkit-scrollbar-thumb {
  border-radius: 8px;
  background: rgba(102, 102, 102, 0.25);
}

.skeleton-table-wrapper::-webkit-scrollbar-track {
  background: #f5f5f5;
}

.skeleton-table-grid {
  display: grid;
  align-items: center;
  width: max-content;
  min-width: 100%;
  box-sizing: border-box;
  padding-right: 16px;
  padding-left: 12px;
  column-gap: 16px;
}

.skeleton-table-header {
  height: 48px;
  background: #f5f5f5;
}

.skeleton-table-row {
  height: 56px;
  background: #ffffff;
  border: 1px solid #f0f6fa;
  border-top: none;
}

.skeleton-action-space {
  width: 104px;
}

.skeleton-row-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.skeleton-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 40px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  padding: 0 24px;
  overflow: hidden;
  background-color: #ffffff;
  border: solid 2px #f5f5f5;
  border-top: none;
  border-radius: 0 0 8px 8px;
}

.skeleton-pagination-right {
  display: flex;
  align-items: center;
  gap: 24px;
}

@media screen and (max-width: 800px) {
  .skeleton-filters {
    flex-direction: column;
    background-color: #ffffff;
  }

  .skeleton-filter-buttons {
    width: 100%;
  }

  .skeleton-filter-buttons > * {
    flex: 1;
  }

  .skeleton-table-wrapper {
    border-right: 1px solid #f0f6fa;
    border-left: 1px solid #f0f6fa;
  }

  .skeleton-pagination {
    padding: 0 8px;
  }

  .skeleton-pagination-right {
    gap: 12px;
  }
}
</style>
